import { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface TokenWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTokens: number;
  threshold?: number;
}

const TokenWarningModal: React.FC<TokenWarningModalProps> = ({
  isOpen,
  onClose,
  currentTokens,
  threshold = 190000
}) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);

  const playWarningSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const ctx = audioContextRef.current;
      
      const oscillator1 = ctx.createOscillator();
      const oscillator2 = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator1.type = 'sawtooth';
      oscillator1.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator1.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.1);
      oscillator1.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.2);

      oscillator2.type = 'square';
      oscillator2.frequency.setValueAtTime(1200, ctx.currentTime);
      oscillator2.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.15);

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

      oscillator1.start(ctx.currentTime);
      oscillator2.start(ctx.currentTime);
      oscillator1.stop(ctx.currentTime + 0.5);
      oscillator2.stop(ctx.currentTime + 0.5);

      setTimeout(() => {
        const osc3 = ctx.createOscillator();
        const osc4 = ctx.createOscillator();
        const gain2 = ctx.createGain();

        osc3.connect(gain2);
        osc4.connect(gain2);
        gain2.connect(ctx.destination);

        osc3.type = 'sawtooth';
        osc3.frequency.setValueAtTime(1000, ctx.currentTime);
        osc3.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.1);

        osc4.type = 'square';
        osc4.frequency.setValueAtTime(1500, ctx.currentTime);

        gain2.gain.setValueAtTime(0.25, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

        osc3.start(ctx.currentTime);
        osc4.start(ctx.currentTime);
        osc3.stop(ctx.currentTime + 0.4);
        osc4.stop(ctx.currentTime + 0.4);
      }, 600);

    } catch (e) {
      console.error('Failed to play warning sound:', e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      playWarningSound();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-gradient-to-br from-red-900 via-red-800 to-orange-900 border-4 border-red-500 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-red-500/50 animate-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-red-500 rounded-full blur-xl opacity-50 animate-pulse" />
            <div className="relative bg-red-600 p-6 rounded-full animate-bounce">
              <AlertTriangle size={48} className="text-white" />
            </div>
          </div>

          <h2 className="text-3xl font-black text-white mb-4 tracking-wide animate-pulse">
            ⚠️ TOKEN 超限警告 ⚠️
          </h2>

          <div className="bg-black/30 rounded-xl p-4 mb-6 w-full">
            <p className="text-red-200 text-lg font-bold mb-2">
              输入 Token 已接近上限！
            </p>
            <div className="flex justify-center items-center gap-3">
              <span className="text-4xl font-black text-yellow-400">
                {currentTokens.toLocaleString()}
              </span>
              <span className="text-2xl text-gray-300">/</span>
              <span className="text-2xl text-gray-400">200,000</span>
            </div>
            <div className="mt-3 bg-gray-700/50 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 transition-all duration-500"
                style={{ width: `${Math.min((currentTokens / 200000) * 100, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-red-950/50 border border-red-500/30 rounded-lg p-4 mb-6">
            <p className="text-white text-base leading-relaxed">
              <span className="text-yellow-400 font-bold">请及时删除本对话中无用的消息！</span>
              <br />
              <span className="text-gray-300 text-sm mt-2 block">
                长对话会消耗大量 Token，建议定期清理不必要的消息以节省成本。
              </span>
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-95"
          >
            我知道了，立即清理
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenWarningModal;
